import { FolderContent } from '@/api/generatedApi';
import { useArchiveDownloadPreview } from '@/hooks/useArchiveDownloadPreview';
import { ArchiveDownloadProgress, useDownloadArchive } from '@/hooks/useDownloadArchive';
import { formatFileSize, pluralize } from '@/utils/format';
import { AssetsPageObjectModel } from '@/poms/assetsPageObjectModel';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ContentTreeItem, itemKey, sortContents } from './DeleteFolderModal/DeletionContentsTree';
import { TitleText } from '../TitleText';

export const BulkDownloadModal = <T extends FolderContent>({
  open,
  onClose,
  selectedContent,
  onDownloadComplete,
}: {
  open: boolean;
  onClose: () => void;
  selectedContent: T[];
  onDownloadComplete?: () => void;
}) => {
  const { downloadArchiveAsync, isPending } = useDownloadArchive();

  const folderIds = useMemo(
    () => selectedContent.filter(content => content.contentType === 'folder').map(content => content.id),
    [selectedContent]
  );
  const assetIds = useMemo(
    () => selectedContent.filter(content => content.contentType === 'asset').map(content => content.id),
    [selectedContent]
  );

  const preview = useArchiveDownloadPreview({ folderIds, assetIds, enabled: open });

  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [progress, setProgress] = useState<ArchiveDownloadProgress | undefined>();
  const [archiveName, setArchiveName] = useState('');
  const abortControllerReference = useRef<AbortController | undefined>(undefined);

  useEffect(() => {
    setProgress(undefined);
    setExpandedItems([]);
    setArchiveName('');
    if (!open) {
      abortControllerReference.current?.abort();
      abortControllerReference.current = undefined;
    }
  }, [open]);

  const sorted = useMemo(() => sortContents(selectedContent as FolderContent[]), [selectedContent]);

  const isOverFileLimit =
    preview.fileCount !== undefined && preview.maxFiles !== undefined && preview.fileCount > preview.maxFiles;

  const handleDownload = async () => {
    setProgress({ loaded: 0, total: undefined });
    const controller = new AbortController();
    abortControllerReference.current = controller;
    try {
      const trimmedName = archiveName.trim();
      await downloadArchiveAsync({
        folderIds,
        assetIds,
        archiveName: trimmedName.length > 0 ? trimmedName : undefined,
        signal: controller.signal,
        onProgress: update => {
          if (!controller.signal.aborted) {
            setProgress(update);
          }
        },
      });
    } catch {
      // Toast / abort handling lives in the hook; we just tear down.
    } finally {
      abortControllerReference.current = undefined;
    }
    onClose();
    onDownloadComplete?.();
  };

  const handleCancel = () => {
    if (isPending) {
      abortControllerReference.current?.abort();
      abortControllerReference.current = undefined;
    }
    onClose();
  };

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={isPending ? undefined : onClose}
      aria-modal='true'
      data-testid={AssetsPageObjectModel.BulkDownloadModal}
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
          },
        },
      }}
    >
      <TitleText sx={{ padding: '24px 8px 0 24px' }}>Download as ZIP</TitleText>
      <DialogContent sx={{ padding: '8px 24px 24px 24px' }}>
        <Typography variant='body2' color='textSecondary' gutterBottom>
          {buildDescription(preview)}
        </Typography>
        {isOverFileLimit && (
          <Alert severity='warning' sx={{ mt: 1 }}>
            This selection has {pluralize(preview.fileCount ?? 0, 'file', 'files')}, which exceeds the{' '}
            {pluralize(preview.maxFiles ?? 0, 'file', 'files')} limit per archive download. Split your selection into
            smaller batches and try again.
          </Alert>
        )}
        <Box
          sx={{
            mt: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            maxHeight: 250,
            overflowY: 'auto',
            p: 1,
          }}
        >
          <SimpleTreeView
            expandedItems={expandedItems}
            onExpandedItemsChange={(_event, itemIds) => setExpandedItems(itemIds)}
            sx={{
              '& .MuiTreeItem-content': {
                borderRadius: 1,
              },
            }}
          >
            {sorted.map(item => (
              <ContentTreeItem key={itemKey(item)} content={item} />
            ))}
          </SimpleTreeView>
        </Box>
        {(isPending || progress) && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant='body2' color='textSecondary'>
                {buildProgressLabel(progress)}
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {buildProgressPercent(progress)}
              </Typography>
            </Box>
            <LinearProgress
              variant={isDeterminate(progress) ? 'determinate' : 'indeterminate'}
              value={isDeterminate(progress) ? (progress!.loaded / progress!.total!) * 100 : undefined}
            />
          </Box>
        )}
        <TextField
          fullWidth
          size='small'
          label='Archive name'
          placeholder='archive'
          value={archiveName}
          onChange={event => setArchiveName(event.target.value)}
          disabled={isPending}
          slotProps={{ htmlInput: { maxLength: 120 } }}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 24px 24px' }}>
        <Button onClick={handleCancel} sx={{ textTransform: 'none', borderRadius: '12px', padding: '8px 16px' }}>
          {isPending ? 'Cancel download' : 'Cancel'}
        </Button>
        <Button
          variant='contained'
          onClick={() => void handleDownload()}
          disabled={isPending || preview.isLoading || (preview.fileCount ?? 0) === 0 || isOverFileLimit}
          sx={{ textTransform: 'none', borderRadius: '12px', padding: '8px 16px', minWidth: '120px' }}
        >
          {isPending ? <CircularProgress size={20} /> : 'Download'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function buildDescription(preview: {
  fileCount?: number;
  subfolderCount?: number;
  isLoading: boolean;
  error?: string;
}): string {
  if (preview.error) {
    return preview.error;
  }
  if (preview.isLoading || preview.fileCount === undefined) {
    return 'Calculating download contents...';
  }
  const { fileCount, subfolderCount = 0 } = preview;
  if (fileCount === 0) {
    return 'No downloadable files in this selection.';
  }
  const fileLabel = pluralize(fileCount, 'file', 'files');
  const suffix = subfolderCount > 0 ? `, including ${pluralize(subfolderCount, 'subfolder', 'subfolders')}` : '';
  return `${fileLabel} will be downloaded as a single ZIP file${suffix}.`;
}

function buildProgressLabel(progress: ArchiveDownloadProgress | undefined): string {
  if (!progress) return 'Preparing download...';
  if (progress.total) {
    return `Downloading — ${formatFileSize(progress.loaded)} of ${formatFileSize(progress.total)}`;
  }
  if (progress.loaded > 0) {
    return `Downloading — ${formatFileSize(progress.loaded)}`;
  }
  return 'Starting download...';
}

function buildProgressPercent(progress: ArchiveDownloadProgress | undefined): string {
  if (!isDeterminate(progress)) return '';
  return `${Math.round((progress!.loaded / progress!.total!) * 100)}%`;
}

function isDeterminate(progress: ArchiveDownloadProgress | undefined): boolean {
  return !!progress && !!progress.total && progress.total > 0;
}
